import React, { createContext, useState, useContext, useEffect } from 'react';
import apiService from '../components/services/apiService';
import { useToast } from './ToastContext';
import { getOrCreateGuestId } from '../utils/guestUtils';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const localCart = localStorage.getItem('local_cart');
      return localCart ? JSON.parse(localCart) : [];
    } catch {
      return [];
    }
  });

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [loadingCart, setLoadingCart] = useState(true);
  const { showToast } = useToast();

  const [couponCode, setCouponCode] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [couponError, setCouponError] = useState('');

  // Lưu sao lưu localStorage
  useEffect(() => {
    try {
      localStorage.setItem('local_cart', JSON.stringify(cart));
    } catch (e) {
      console.error("Lỗi lưu local_cart:", e);
    }
  }, [cart]);

 // Lấy danh sách giỏ hàng khi F5 (Đồng bộ sạch từ Server)
useEffect(() => {
    const fetchCartFromServer = async () => {
      try {
        const guestId = getOrCreateGuestId();
        const headers = { 'X-Guest-Id': guestId };

        const response = await apiService.get(`/cart?guestId=${guestId}`, { headers });
        const data = response?.data !== undefined ? response.data : response;

        if (Array.isArray(data) && data.length > 0) { // CHỈ CẬP NHẬT KHI SERVER CÓ DỮ LIỆU
          const normalizedServerCart = data
            .filter(item => item && (item.productId || item.id))
            .map((item) => {
              const itemPrice = Number(item.flashSalePrice || item.price) || 0;

              return {
                ...item,
                id: item.id || item.productId,
                productId: item.productId || item.id,
                productVariantId: item.productVariantId || item.variantId || item.selectedVariantId || item.idVariant || null,
                quantity: Math.max(1, parseInt(item.quantity) || 1),
                price: itemPrice,
                thumbnail: item.thumbnail || item.image || item.images?.[0] || '',
                selectedColor: item.color || item.selectedColor || 'default',
                selectedSize: item.size || item.selectedSize || 'default',
                isFlashSale: item.isFlashSale || item.flashSalePrice ? true : false,
                flashSaleId: item.flashSaleId || item.id,
                flashSalePrice: item.flashSalePrice || itemPrice,
              };
            });

          setCart(normalizedServerCart);
        }
      } catch (error) {
        console.error("Lỗi khi tải giỏ hàng từ server:", error);
      } finally {
        setLoadingCart(false);
      }
    };

    fetchCartFromServer();
  }, []);


  const addToCart = async (product, param2 = 1, param3 = null, param4 = null) => {
    if (!product) return;

    let quantity = 1;
    let image = '';
    let color = null;
    let size = null;

    if (typeof param2 === 'number') {
      quantity = Math.max(1, param2);
      color = param3;
      size = param4;
      image = product.thumbnail || product.image || product.images?.[0] || '';
    } else if (typeof param2 === 'string' && (param2.startsWith('http') || param2.startsWith('/'))) {
      image = param2;
      quantity = Math.max(1, parseInt(param3) || 1);
      color = param4;
      size = null;
    } else {
      quantity = 1;
      image = product.thumbnail || product.image || product.images?.[0] || '';
    }

    const productId = product.id || product.productId || product.realId;
    if (!productId) {
      console.error("Lỗi: Sản phẩm không có ID hợp lệ", product);
      return;
    }

    // Chỉ lấy variantId nếu sản phẩm thực sự truyền vào, không tự động bốc linh tinh sản phẩm đầu tiên nếu không khớp
    let productVariantId = product.productVariantId || product.variantId || product.selectedVariantId || null;
    
    // Nếu sản phẩm có danh sách biến thể cụ thể và người dùng chưa chọn, hãy yêu cầu chọn hoặc lấy đúng variant tương ứng
    if (!productVariantId && Array.isArray(product.productVariants) && product.productVariants.length === 1) {
      productVariantId = product.productVariants[0].id; // Chỉ tự động lấy khi sản phẩm đó CHỈ CÓ DUY NHẤT 1 biến thể
    }
    const selectedColor = (color && color.trim() !== '') ? color : (product.colors?.[0] || 'default');
    const selectedSize = (size && size.trim() !== '') ? size : (product.sizes?.[0] || 'default');
    const productPrice = Number(product.price) || 0;

    setCart((prevCart) => {
      const existingIndex = prevCart.findIndex(
          (item) =>
              (String(item.id) === String(productId) || String(item.productId) === String(productId)) &&
              String(item.productVariantId) === String(productVariantId) &&
              String(item.selectedColor || item.color || 'default') === String(selectedColor) &&
              String(item.selectedSize || item.size || 'default') === String(selectedSize)
      );

      let updated;
      if (existingIndex > -1) {
        updated = [...prevCart];
        const currentQty = parseInt(updated[existingIndex].quantity) || 0;
        updated[existingIndex].quantity = currentQty + quantity;
      } else {
        updated = [
          ...prevCart,
          {
            ...product,
            id: productId,
            productId: productId,
            productVariantId:productVariantId,
            name: product.name,
            price: productPrice,
            thumbnail: image,
            image: image,
            quantity: quantity,
            selectedColor,
            selectedSize,
            isFlashSale: product.isFlashSale || product.flashSalePrice ? true : false,
            flashSaleId: product.flashSaleId || product.id,
            flashSalePrice: product.flashSalePrice || productPrice,
          },
        ];
      }
      return updated;
    });

    setIsCartOpen(true);

    try {
      const guestId = getOrCreateGuestId();
      const payload = {
        productId,
        productVariantId,
        image,
        quantity,
        price: product.flashSalePrice || product.price,
        color: selectedColor,
        size: selectedSize,
      };

      const headers = { 'X-Guest-Id': guestId };
      const response = await apiService.post('/cart/add', payload, { headers });
      const resData = response?.data !== undefined ? response.data : response;

      if (resData && resData.success === false) {
        showToast(resData.message || "Thêm vào giỏ thất bại", "error");
      } else {
        showToast(resData?.message || "Đã thêm sản phẩm vào giỏ hàng!", "success");
      }
    } catch (error) {
      console.error("Lỗi đồng bộ thêm giỏ hàng lên Backend:", error);
    }
  };

  const updateCartQuantity = (index, newQuantity) => {
    setCart((prevCart) => {
      const updated = [...prevCart];
      if (updated[index]) {
        updated[index].quantity = newQuantity;
      }
      return updated;
    });
  };

  const commitCartQuantity = async (index) => {
    const targetItem = cart[index];
    if (!targetItem) return;

    let qty = parseInt(targetItem.quantity);

    if (isNaN(qty) || qty <= 0) {
      qty = 1;
      showToast("Số lượng tối thiểu là 1", "info");
    }

    setCart((prevCart) => {
      const updated = [...prevCart];
      if (updated[index]) {
        updated[index].quantity = qty;
      }
      return updated;
    });

    try {
      const guestId = getOrCreateGuestId();
      const payload = {
        productId: targetItem.id || targetItem.productId,
        productVariantId:targetItem.productVariantId,
        quantity: qty,
        price: targetItem.flashSalePrice || targetItem.price,
        color: targetItem.selectedColor || targetItem.color || 'default',
        size: targetItem.selectedSize || targetItem.size || 'default',
      };

      await apiService.put('/cart/update', payload, { headers: { 'X-Guest-Id': guestId } });
    } catch (error) {
      console.error("Lỗi cập nhật số lượng:", error);
    }
  };

const removeFromCart = async (index) => {
    const itemToRemove = cart[index];
    if (!itemToRemove) return;

    const productId = itemToRemove.id || itemToRemove.productId;
    const variantId = itemToRemove.productVariantId || itemToRemove.variantId || '0';
    const color = itemToRemove.selectedColor || itemToRemove.color || 'default';
    const size = itemToRemove.selectedSize || itemToRemove.size || 'default';
    const fieldKey = `${productId}_${variantId}_${color}_${size}`;

    // Cập nhật state local ngay lập tức
    setCart((prevCart) => prevCart.filter((_, i) => i !== index));

    try {
      const guestId = getOrCreateGuestId();
      
      // 👉 HOÀN LẠI KHO FLASH SALE KHI XÓA ĐƠN LẺ SẢN PHẨM FLASH SALE
      if (itemToRemove.flashSalePrice || itemToRemove.isFlashSale) {
        const targetFlashSaleId = itemToRemove.flashSaleId || itemToRemove.id;
        const quantityToRelease = itemToRemove.quantity || 1;
        await apiService.post(`/flash-sale/release?flashSaleProductId=${targetFlashSaleId}&quantity=${quantityToRelease}`);
        window.dispatchEvent(new Event('flashSaleUpdated')); // Phát sự kiện để component FlashSale load lại dữ liệu
      }

      await apiService.delete(`/cart/remove/${fieldKey}`, { headers: { 'X-Guest-Id': guestId } });
      showToast("Đã xóa sản phẩm khỏi giỏ hàng!", "success");
    } catch (error) {
      console.error("Lỗi khi xóa sản phẩm trên server:", error);
    }
  };

  const clearCart = async () => {
    const previousCart = [...cart];
    setCart([]);
    localStorage.removeItem('local_cart');

    try {
      const guestId = getOrCreateGuestId();
      
      // 👉 HOÀN LẠI KHO FLASH SALE CHO TẤT CẢ SẢN PHẨM FLASH SALE TRONG GIỎ KHI XÓA HẾT
      for (const item of previousCart) {
        if (item.flashSalePrice || item.isFlashSale) {
          const flashSaleId = item.flashSaleId || item.id;
          const quantityToRelease = item.quantity || 1;
          await apiService.post(`/flash-sale/release?flashSaleProductId=${flashSaleId}&quantity=${quantityToRelease}`);
        }
      }
      window.dispatchEvent(new Event('flashSaleUpdated')); // Báo giao diện FlashSale cập nhật lại số lượng tồn kho

      await apiService.delete('/cart/clear', { headers: { 'X-Guest-Id': guestId } });
      showToast("Đã làm sạch giỏ hàng!", "success");
    } catch (error) {
      console.error("Lỗi khi xóa toàn bộ giỏ hàng trên server:", error);
    }
  };

  const handleApplyCoupon = (e) => {
    e.preventDefault();
    if (couponCode.trim().toUpperCase() === 'JapanICHIBA10') {
      setDiscountPercent(10);
      setCouponError('');
    } else {
      setCouponError('Mã giảm giá không hợp lệ (Thử: JapanICHIBA10)');
    }
  };

  const subtotal = cart.reduce((sum, item) => {
    const p = Number(item.price) || 0;
    const q = Math.max(1, parseInt(item.quantity) || 1);
    return sum + p * q;
  }, 0);

  const discountAmount = (subtotal * discountPercent) / 100;
  const shippingFee = subtotal > 5000000 || subtotal === 0 ? 0 : 30000;
  const finalTotal = Math.max(0, subtotal - discountAmount + shippingFee);

  return (
      <CartContext.Provider
          value={{
            cart,
            setCart,
            isCartOpen,
            setIsCartOpen,
            loadingCart,
            addToCart,
            updateCartQuantity,
            removeFromCart,
            commitCartQuantity,
            clearCart,
            couponCode,
            setCouponCode,
            couponError,
            handleApplyCoupon,
            subtotal,
            discountAmount,
            shippingFee,
            finalTotal,
          }}
      >
        {children}
      </CartContext.Provider>
  );
};

export const useCartContext = () => useContext(CartContext);